import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Moon,
  Volume2,
  Bell,
  Wifi,
  User,
  Shield,
  Cookie,
  Mail,
  Info,
  ChevronRight,
} from 'lucide-react';
import { useAudioStore } from '@/hooks/useAudioStore';
import { resetConsent } from '@/utils/consent';

export const SettingsScreen: React.FC = () => {
  const { settings, updateSettings } = useAudioStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLink = (id: string) => {
    switch (id) {
      case 'contact':
        window.location.href = 'mailto:info@suton-radio.de';
        break;
      case 'privacy':
        navigate('/datenschutz');
        break;
      case 'account':
        // SETTINGS-FIX: "Račun" više nije sync nego shortcut na Omiljeno
        navigate('/app/favorites');
        break;
      case 'cookies':
        // GDPR: korisnik može promijeniti cookie odluku u bilo kom trenutku.
        // Briše localStorage suton-consent + GA back to denied + custom event
        // koji ponovo prikaže CookieConsent banner (bez page reload-a, audio
        // se ne prekida).
        resetConsent();
        break;
    }
  };

  const sections = [
    {
      titleKey: 'settings.audio',
      items: [
        {
          id: 'autoplay',
          icon: <Volume2 className="w-5 h-5 text-accent-blue" />,
          titleKey: 'settings.autoplay',
          subtitleKey: 'settings.autoplaySub',
          type: 'toggle' as const,
          value: settings.autoPlay,
          onChange: (value: boolean) => updateSettings({ autoPlay: value }),
        },
        {
          id: 'normalize',
          icon: <Volume2 className="w-5 h-5 text-accent-cyan" />,
          titleKey: 'settings.normalize',
          subtitleKey: 'settings.normalizeSub',
          type: 'toggle' as const,
          value: settings.normalizeVolume,
          onChange: (value: boolean) => updateSettings({ normalizeVolume: value }),
        },
        {
          id: 'quality',
          icon: <Wifi className="w-5 h-5 text-accent-purple" />,
          titleKey: 'settings.quality',
          subtitleKey: 'settings.qualitySub',
          type: 'value' as const,
          valueText: '128 kbps',
        },
      ],
    },
    {
      titleKey: 'settings.playback',
      items: [
        // SETTINGS-FIX: Crossfade hidden until implemented (kod ostaje za kasnije)
        // {
        //   id: 'crossfade',
        //   icon: <Clock className="w-5 h-5 text-accent-pink" />,
        //   titleKey: 'settings.crossfade',
        //   subtitleKey: 'settings.crossfadeSub',
        //   type: 'toggle' as const,
        //   value: settings.crossfade,
        //   onChange: (value: boolean) => updateSettings({ crossfade: value }),
        // },
        {
          id: 'notifications',
          icon: <Bell className="w-5 h-5 text-warning" />,
          titleKey: 'settings.notifications',
          subtitleKey: 'settings.notificationsSub',
          type: 'toggle' as const,
          value: settings.notifications,
          onChange: (value: boolean) => updateSettings({ notifications: value }),
        },
      ],
    },
    {
      titleKey: 'settings.app',
      items: [
        {
          id: 'darkmode',
          icon: <Moon className="w-5 h-5 text-text-secondary" />,
          titleKey: 'settings.darkTheme',
          subtitleKey: 'settings.darkThemeSub',
          type: 'toggle' as const,
          value: true,
          onChange: () => {},
        },
        {
          id: 'account',
          icon: <User className="w-5 h-5 text-accent-blue" />,
          titleKey: 'settings.account',
          subtitleKey: 'settings.accountSub',
          type: 'link' as const,
        },
        {
          id: 'privacy',
          icon: <Shield className="w-5 h-5 text-success" />,
          titleKey: 'settings.privacy',
          subtitleKey: 'settings.privacySub',
          type: 'link' as const,
        },
        {
          id: 'cookies',
          icon: <Cookie className="w-5 h-5 text-warning" />,
          titleKey: 'settings.cookies',
          subtitleKey: 'settings.cookiesSub',
          type: 'link' as const,
        },
      ],
    },
    {
      titleKey: 'settings.support',
      items: [
        // SETTINGS-FIX: FAQ/Help link uklonjen (nema FAQ stranice za sada)
        {
          id: 'contact',
          icon: <Mail className="w-5 h-5 text-accent-pink" />,
          titleKey: 'settings.contact',
          subtitleKey: 'settings.contactSub',
          type: 'link' as const,
        },
        {
          id: 'about',
          icon: <Info className="w-5 h-5 text-text-secondary" />,
          titleKey: 'settings.about',
          subtitleKey: 'settings.version',
          type: 'value' as const,
          valueTextKey: 'settings.version',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen px-4 pt-6 pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h1 className="text-3xl font-bold">{t('settings.title')}</h1>
      </motion.div>

      {/* Settings Sections */}
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => (
          <motion.div
            key={section.titleKey}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + sectionIndex * 0.1 }}
          >
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3 ml-1">
              {t(section.titleKey)}
            </h2>
            <div className="glass rounded-2xl overflow-hidden">
              {section.items.map((item, itemIndex) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-4 ${
                    itemIndex !== section.items.length - 1 ? 'border-b border-white/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-medium">{t(item.titleKey)}</p>
                      {item.subtitleKey && (
                        <p className="text-xs text-text-muted">{t(item.subtitleKey)}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    {item.type === 'toggle' && (
                      <button
                        onClick={() => item.onChange?.(!item.value)}
                        className={`w-12 h-6 rounded-full transition-colors relative ${
                          item.value ? 'bg-accent-blue' : 'bg-white/20'
                        }`}
                      >
                        <motion.div
                          animate={{ x: item.value ? 24 : 2 }}
                          className="w-5 h-5 rounded-full bg-white absolute top-0.5"
                        />
                      </button>
                    )}
                    {item.type === 'value' && (
                      <span className="text-sm text-text-secondary">
                        {'valueTextKey' in item && item.valueTextKey
                          ? t(item.valueTextKey)
                          : 'valueText' in item && item.valueText}
                      </span>
                    )}
                    {item.type === 'link' && (
                      <button
                        onClick={() => handleLink(item.id)}
                        className="hover:bg-white/5 rounded-lg p-1 -m-1 transition-colors"
                        aria-label={`${t('common.open')} ${t(item.titleKey)}`}
                      >
                        <ChevronRight className="w-5 h-5 text-text-muted" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center mt-12 text-xs text-text-muted"
      >
        <p>{t('settings.footer')}</p>
        <p>{t('settings.footerLocation')}</p>
      </motion.div>
    </div>
  );
};
